import { useTranslation } from 'react-i18next';
import { CompanyBriefData } from '@/features/company-brief-data';
import { CompanyAdditionalData } from '@/features/company-additional-data';
import { useGetBriefDataQuery } from '@/app/api';
import { FC, useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

interface Props {
  tin: string;
  defaultEnabled?: boolean;
  type?: 'buyer' | 'seller';
}

const SubjectInfoTabs: FC<Props> = ({ tin, type, defaultEnabled }) => {
  const defaultTabIndex = 0;
  const { t } = useTranslation();
  const uniqueKey = type ? `${type}U` : 'u';
  const unique = new URLSearchParams(window.location.search).get(uniqueKey);

  const [isBriefdataEnabled, setIsBriefdataEnabled] = useState(defaultEnabled ? true : !!unique);
  const [tabIndex, setTabIndex] = useState(defaultTabIndex);
  const { data, isFetching, error, refetch } = useGetBriefDataQuery(tin, {
    skip: !tin || (!isBriefdataEnabled && !unique),
  });

  useEffect(() => {
    if (data?.data) {
      const url = new URL(window.location.href);
      url.searchParams.set(uniqueKey, String(data.data.unique));
      window.history.replaceState({}, '', url.toString());
    }
  }, [data, uniqueKey]);

  useEffect(() => {
    setTabIndex(defaultTabIndex);
  }, [tin]);

  const handleBriefDataLoad = () => {
    setIsBriefdataEnabled(true);
  };

  const renderTabContent = () => {
    switch (tabIndex) {
      case 1:
        if (!data?.data?.briefData) return null;
        return (
          <CompanyAdditionalData
            unique={data?.data?.unique}
            tin={data?.data?.briefData?.tin}
            isBriefDataLoading={isFetching}
            isBriefDataError={!!error || !data?.data?.briefData}
          />
        );
      default:
        return (
          <CompanyBriefData
            data={data?.data?.briefData}
            onClick={handleBriefDataLoad}
            isLoading={isFetching}
            error={error}
            refetch={refetch}
            emptyTitle={
              type === 'buyer'
                ? t('ihamkor_empty_data_title_buyer')
                : t('ihamkor_empty_data_title_supplier')
            }
          />
        );
    }
  };

  const tabs = [t('general_data'), t('financial_indicators')];

  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: '1px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flexGrow: 1,
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        sx={{
          backgroundColor: 'rgba(13, 29, 88, 0.04)',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            label={tab}
            onClick={() => setTabIndex(index)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: '#FFF',
                color: 'text.primary',
              },
            }}
          />
        ))}
      </Tabs>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          px: 2,
          py: 3,
        }}
      >
        {renderTabContent()}
      </Box>
    </Box>
  );
};

export default SubjectInfoTabs;
